import * as z from "zod";

export const COMPANY_EMPLOYEE_LIMIT = 20;

export const CustomCompanyPositionSchema = z.object({
  title: z.string().trim().min(1).max(80),
  summary: z.string().trim().min(1).max(500),
  responsibilities: z.array(z.string().trim().min(1).max(400)).min(1).max(8),
  requiredSkills: z.array(z.string().trim().min(1).max(200)).min(1).max(8),
  deliverables: z.array(z.string().trim().min(1).max(300)).min(1).max(8),
  acceptanceCriteria: z.array(z.string().trim().min(1).max(400)).min(1).max(8),
  escalation: z.string().trim().min(1).max(800),
  instructions: z.string().trim().min(1).max(4000),
});

export const CompanyHiringProposalInputSchema = z
  .object({
    proposalSlot: z.number().int().min(1).max(2),
    positionId: z.string().trim().min(1).max(80).optional(),
    customPosition: CustomCompanyPositionSchema.optional(),
    rationale: z.string().trim().min(1).max(1200),
    evidence: z.string().trim().min(1).max(2000),
    coverageGap: z.string().trim().min(1).max(1200),
    additionalInstructions: z.string().trim().max(4000).optional(),
    firstTask: z.string().trim().min(1).max(1600),
    firstTaskAcceptanceCriteria: z.string().trim().min(1).max(1600),
  })
  .refine((input) => Boolean(input.positionId) !== Boolean(input.customPosition), {
    message: "Choose one template position or one custom position",
  });

export const CompanyHiringSnapshotSchema = z.object({
  custom: z.boolean(),
  positionId: z.string(),
  positionVersion: z.number().int().positive(),
  title: z.string(),
  summary: z.string(),
  responsibilities: z.array(z.string()),
  requiredSkills: z.array(z.string()),
  deliverables: z.array(z.string()),
  acceptanceCriteria: z.array(z.string()),
  escalation: z.string(),
  instructions: z.string(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  rationale: z.string(),
  evidence: z.string(),
  coverageGap: z.string(),
  additionalInstructions: z.string().nullable(),
  firstTask: z.string(),
  firstTaskAcceptanceCriteria: z.string(),
});

const CompanyHiringEmployeeSchema = z.object({ id: z.string(), name: z.string() });
const CompanyHiringTicketSchema = z.object({
  id: z.string(),
  ref: z.string(),
  title: z.string(),
  description: z.string(),
});

export const CompanyHiringPositionSchema = z.object({
  custom: z.boolean(),
  id: z.string(),
  version: z.number().int().positive(),
  title: z.string(),
  summary: z.string(),
  responsibilities: z.array(z.string()),
  requiredSkills: z.array(z.string()),
  deliverables: z.array(z.string()),
  acceptanceCriteria: z.array(z.string()),
  escalation: z.string(),
  status: z.enum(["available", "pending", "filled"]),
  employee: CompanyHiringEmployeeSchema.nullable(),
});

export const CompanyHiringRequestSchema = z.object({
  id: z.string(),
  revision: z.number().int().positive(),
  proposalSlot: z.number().int().min(1).max(2),
  status: z.enum(["pending", "hired", "declined"]),
  snapshot: CompanyHiringSnapshotSchema,
  createdAt: z.string().datetime(),
  decidedAt: z.string().datetime().nullable(),
  employee: CompanyHiringEmployeeSchema.nullable(),
  firstTicket: CompanyHiringTicketSchema.nullable(),
});

export const CompanyHiringOverviewSchema = z.object({
  spaceId: z.string(),
  companyName: z.string(),
  platform: z.enum(["tiktok", "ozon"]),
  canApprove: z.boolean(),
  memberCount: z.number().int().nonnegative(),
  memberLimit: z.number().int().positive(),
  positions: z.array(CompanyHiringPositionSchema),
  requests: z.array(CompanyHiringRequestSchema),
});
export type CompanyHiringOverview = z.infer<typeof CompanyHiringOverviewSchema>;
export type CompanyHiringRequest = z.infer<typeof CompanyHiringRequestSchema>;

export type CompanyHiringSnapshot = z.infer<typeof CompanyHiringSnapshotSchema>;

export const CompanyHiringDecisionInputSchema = z.object({
  spaceId: z.string(),
  requestId: z.string(),
  revision: z.number().int().positive(),
});

export const CompanyHiringDecisionSchema = z.object({
  status: z.enum(["hired", "declined"]),
  requestId: z.string(),
  revision: z.number().int().positive(),
  employee: CompanyHiringEmployeeSchema.nullable(),
  firstTicket: CompanyHiringTicketSchema.nullable(),
});

export const CompanyEmployeeArchiveInputSchema = z.object({
  spaceId: z.string(),
  botId: z.string(),
});

export const CompanyEmployeeArchiveSchema = z.object({
  ok: z.literal(true),
  unassignedTickets: z.number().int().nonnegative(),
});
